import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrgId } from "@src/api/organizations/get-organization";
import { SmartPricingKickoffDialog } from "@src/components/BillingSettings2026/components/dialogs/SmartPricingKickoffDialog";
import {
  type BillingGateModuleViewModel,
  useBillingGateModel,
} from "@src/components/BillingSettings2026/workflow";
import { SMART_PRICING_VARIANTS } from "@src/constants/billing";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { SettingsSectionId } from "@src/types/navigation";
import { contactSmartPricingSpecialist } from "@src/utils/smartPricingContact";

import { PricingGatedModalController } from "./PricingGatedModal.controller";

type BypassDecision = Extract<BillingGateModuleViewModel, { kind: "bypass" }>;

const isUnlimitedPricingBypass = (
  decision: BillingGateModuleViewModel
): decision is BypassDecision =>
  decision.kind === "bypass" &&
  decision.reason === "unlimited" &&
  SMART_PRICING_VARIANTS.has(decision.moduleId);

type PricingGatedModalRouteProps = {
  moduleIds: readonly string[];
  children: ReactNode;
};

/**
 * Renders children only when every listed billing module is bypassed. Fails
 * closed while the gate model loads or any module lacks a decision; otherwise
 * shows the modal for the first gated module.
 */
export const GatedModalRoute = ({
  moduleIds,
  children,
}: PricingGatedModalRouteProps) => {
  const navigate = useNavigate();
  const gateModel = useBillingGateModel({ enabled: true });

  const orgId = useGetOrgId();
  const startPromptStorageKey = `ig-smart-pricing-start-prompt-dismissed:${orgId}`;
  const [isStartPromptDismissed, setIsStartPromptDismissed] = useState(
    () => localStorage.getItem(startPromptStorageKey) === "true"
  );

  const priceTestsQuery = useGetExperiencesList({
    module: "pricing",
  });

  const dismissStartPrompt = () => {
    localStorage.setItem(startPromptStorageKey, "true");
    setIsStartPromptDismissed(true);
  };

  if (gateModel.status !== "ready") {
    return null;
  }

  const decisions = moduleIds.map((moduleId) => gateModel.byModuleId[moduleId]);

  if (decisions.some((decision) => !decision)) {
    return null;
  }

  const gated = decisions.find((decision) => decision.kind === "gate");

  if (!gated) {
    const hasStartedPriceTest = !!priceTestsQuery.data?.experiencesList.length;

    // Unlimited subscribers hitting a Smart Pricing surface for the first
    // time get a one-time "start price testing" kickoff prompt
    const unlimitedPricing = decisions.find(isUnlimitedPricingBypass);
    const isBeforeFirstPriceTest =
      !isStartPromptDismissed &&
      priceTestsQuery.isSuccess &&
      !hasStartedPriceTest;

    return (
      <>
        {children}
        {unlimitedPricing && isBeforeFirstPriceTest && (
          <SmartPricingKickoffDialog
            open
            variant="startPriceTesting"
            onClose={dismissStartPrompt}
            onSecondaryAction={dismissStartPrompt}
            onConsultSpecialist={() => {
              contactSmartPricingSpecialist(
                unlimitedPricing.segment === "existing_customer"
              );
              dismissStartPrompt();
            }}
          />
        )}
      </>
    );
  }

  return (
    <PricingGatedModalController
      decision={gated}
      moduleId={gated.moduleId}
      open={true}
      onClose={() => navigate(-1)}
      onContinueToPayment={() =>
        void navigate(
          `${SETTINGS_ROUTES.Settings}?selected=${gated.moduleId}#${SettingsSectionId.Billing}`
        )
      }
    />
  );
};
